import React, { useCallback, useLayoutEffect, useState } from "react";

import { Box, BoxProps } from "@catchoftheday/cg-components";
import { keyframes, styled } from "@catchoftheday/theme";

import { MOBILE_BOTTOM_NAV_HEIGHT } from "@header/consts";

const fadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;
const fadeOut = keyframes`
    0% { opacity: 1; }
    100% { opacity: 0; }
`;

type MenuContentContainerProps = BoxProps & {
  isOpen: boolean;
};

const MenuContentContainer = styled(Box)<MenuContentContainerProps>`
  display: block;
  height: 100%;
  animation: ${(props) => (props.isOpen ? fadeIn : fadeOut)} 200ms ease;
`;

export type MenuContentPopupProps = MenuContentContainerProps & {
  /** Whether to cover the screen with the popup */
  cover?: boolean;
  children: React.ReactNode;
};

export const MenuContentPopup = ({
  isOpen,
  cover,
  children,
  ...props
}: MenuContentPopupProps) => {
  const [windowYOffset, setWindowYOffset] = useState<number | undefined>();
  const [popupHeight, setPopupHeight] = useState("100%");
  const [headerHeight, setHeaderHeight] = useState<string | undefined>();

  const fetchPopupHeight = useCallback(() => {
    const headerElement = window.document.querySelector(
      "#primary-header"
    ) as HTMLDivElement;
    const headerHeight =
      headerElement !== null ? headerElement.offsetHeight : 0;

    setHeaderHeight(`${headerHeight}px`);
    setPopupHeight(
      `${
        window.innerHeight -
        (cover ? 0 : headerHeight) -
        MOBILE_BOTTOM_NAV_HEIGHT
      }px`
    );
  }, [cover]);

  useLayoutEffect(() => {
    fetchPopupHeight();
    window.addEventListener("resize", fetchPopupHeight);

    if (isOpen && typeof window !== "undefined") {
      setWindowYOffset(window.pageYOffset);
      window.document.body.style.position = "fixed";
      window.document.body.style.overflowY = "hidden";
      window.document.body.style.maxWidth = "100%";
    } else {
      window.document.body.style.position = "";
      window.document.body.style.overflowY = "";
      window.document.body.style.maxWidth = "";
      if (windowYOffset) {
        window.scrollTo(0, windowYOffset);
      }
    }

    return () => {
      window.removeEventListener("resize", fetchPopupHeight);
      window.document.body.style.overflowY = "";
      if (windowYOffset) {
        window.scrollTo(0, windowYOffset);
      }
    };
  }, [isOpen, windowYOffset, fetchPopupHeight]);

  return (
    <Box
      display={isOpen ? "block" : "none"}
      position="fixed"
      top={cover ? "0px" : headerHeight}
      left="0px"
      pb={MOBILE_BOTTOM_NAV_HEIGHT}
      height={popupHeight}
      width="100%"
      bg="white"
      zIndex={50}
      {...props}
    >
      <MenuContentContainer isOpen={isOpen}>{children}</MenuContentContainer>
    </Box>
  );
};
